import React, { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Add, Delete, Email, Send } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { issueInvoices } from "./api";
import type { ClubMember, DefaultFeeBasis, InvoiceMemberData } from "./types";
import DialogHeader from "./DialogHeader";

export interface IssueInvoiceTarget {
  clubId: number;
  groupName: string;
  groupMembers: ClubMember[];
  clubMembers: ClubMember[];
  defaultFee: number;
  defaultFeeBasis: DefaultFeeBasis;
}

interface IssueInvoiceDialogProps {
  target: IssueInvoiceTarget | null;
  onClose: () => void;
  onSuccess: () => Promise<void> | void;
  onError: (message: string) => void;
}

const trainingDateKey = (date: Dayjs) => date.format("DD/MM/YYYY HH:mm");

const formatTrainingDateLabel = (dateKey: string) =>
  dayjs(dateKey, "DD/MM/YYYY HH:mm").format("DD MMM YYYY, HH:mm");

const calculateMemberFee = (
  defaultFee: number,
  defaultFeeBasis: DefaultFeeBasis,
  trainingDateKeys: string[],
  excludedTrainingDates: string[],
): string => {
  if (defaultFeeBasis === "invoice") {
    return defaultFee.toFixed(2);
  }
  const includedCount =
    trainingDateKeys.length - excludedTrainingDates.length;
  return (defaultFee * includedCount).toFixed(2);
};

const recalculateMemberFees = (
  members: InvoiceMemberData[],
  trainingDateKeys: string[],
  defaultFee: number,
  defaultFeeBasis: DefaultFeeBasis,
  forceAll = false,
): InvoiceMemberData[] =>
  members.map((member) => {
    if (member.feeEdited && !forceAll) return member;
    return {
      ...member,
      fee: calculateMemberFee(
        defaultFee,
        defaultFeeBasis,
        trainingDateKeys,
        member.excludedTrainingDates,
      ),
      feeEdited: false,
    };
  });

interface MemberTrainingDatesProps {
  member: InvoiceMemberData;
  trainingDateKeys: string[];
  onToggle: (handlerId: number, dateKey: string) => void;
}

const MemberTrainingDates: React.FC<MemberTrainingDatesProps> = ({
  member,
  trainingDateKeys,
  onToggle,
}) => {
  if (trainingDateKeys.length === 0) return null;

  const includedCount =
    trainingDateKeys.length - member.excludedTrainingDates.length;

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
        Training sessions ({includedCount}/{trainingDateKeys.length})
      </Typography>
      <FormGroup row sx={{ gap: 0.5 }}>
        {trainingDateKeys.map((dateKey) => {
          const included = !member.excludedTrainingDates.includes(dateKey);
          return (
            <FormControlLabel
              key={dateKey}
              control={
                <Checkbox
                  size="small"
                  checked={included}
                  onChange={() => onToggle(member.handler_id, dateKey)}
                />
              }
              label={
                <Typography variant="caption">
                  {formatTrainingDateLabel(dateKey)}
                </Typography>
              }
              sx={{ mr: 1.5 }}
            />
          );
        })}
      </FormGroup>
    </Box>
  );
};

const IssueInvoiceDialog: React.FC<IssueInvoiceDialogProps> = ({
  target,
  onClose,
  onSuccess,
  onError,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [dueDate, setDueDate] = useState<Dayjs | null>(null);
  const [trainingDates, setTrainingDates] = useState<Dayjs[]>([]);
  const [newTrainingDate, setNewTrainingDate] = useState<Dayjs | null>(null);
  const [defaultTrainingTime, setDefaultTrainingTime] = useState<Dayjs>(() =>
    dayjs().hour(18).minute(0).second(0).millisecond(0),
  );
  const [invoiceMembers, setInvoiceMembers] = useState<InvoiceMemberData[]>([]);
  const [issuing, setIssuing] = useState(false);

  useEffect(() => {
    if (target) {
      const now = dayjs();
      setStartDate(now.startOf("month"));
      setEndDate(now.endOf("month"));
      setDueDate(now.add(7, "day"));
      setTrainingDates([]);
      setNewTrainingDate(null);
      setInvoiceMembers(
        target.groupMembers.map((m) => ({
          ...m,
          fee: calculateMemberFee(
            target.defaultFee,
            target.defaultFeeBasis,
            [],
            [],
          ),
          feeEdited: false,
          excludedTrainingDates: [],
        })),
      );
    }
  }, [target]);

  useEffect(() => {
    if (!target) return;
    const dateKeys = trainingDates.map(trainingDateKey);
    setInvoiceMembers((prev) =>
      recalculateMemberFees(
        prev,
        dateKeys,
        target.defaultFee,
        target.defaultFeeBasis,
      ),
    );
    // Target init is handled in the effect above; this only reacts to session changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trainingDates]);

  const handleAddTrainingDate = () => {
    if (
      newTrainingDate &&
      !trainingDates.some((d) => d.isSame(newTrainingDate, "minute"))
    ) {
      setTrainingDates(
        [...trainingDates, newTrainingDate].sort(
          (a, b) => a.valueOf() - b.valueOf(),
        ),
      );
      setNewTrainingDate(null);
    }
  };

  const handleRemoveTrainingDate = (index: number) => {
    const removedKey = trainingDateKey(trainingDates[index]);
    setTrainingDates(trainingDates.filter((_, i) => i !== index));
    setInvoiceMembers((prev) =>
      prev.map((m) => ({
        ...m,
        excludedTrainingDates: m.excludedTrainingDates.filter(
          (key) => key !== removedKey,
        ),
      })),
    );
  };

  const handleToggleMemberTrainingDate = (handlerId: number, dateKey: string) => {
    if (!target) return;
    setInvoiceMembers((prev) => {
      const updated = prev.map((m) => {
        if (m.handler_id !== handlerId) return m;
        const excluded = m.excludedTrainingDates.includes(dateKey);
        return {
          ...m,
          excludedTrainingDates: excluded
            ? m.excludedTrainingDates.filter((key) => key !== dateKey)
            : [...m.excludedTrainingDates, dateKey],
        };
      });
      return recalculateMemberFees(
        updated,
        trainingDateKeys,
        target.defaultFee,
        target.defaultFeeBasis,
      );
    });
  };

  const handleUpdateFee = (handlerId: number, fee: string) => {
    setInvoiceMembers((prev) =>
      prev.map((m) =>
        m.handler_id === handlerId ? { ...m, fee, feeEdited: true } : m,
      ),
    );
  };

  const handleRemoveFromInvoice = (handlerId: number) => {
    setInvoiceMembers((prev) => prev.filter((m) => m.handler_id !== handlerId));
  };

  const handleAddToInvoice = (member: ClubMember) => {
    if (!target) return;
    setInvoiceMembers((prev) => [
      ...prev,
      {
        ...member,
        fee: calculateMemberFee(
          target.defaultFee,
          target.defaultFeeBasis,
          trainingDateKeys,
          [],
        ),
        feeEdited: false,
        excludedTrainingDates: [],
      },
    ]);
  };

  const handleApplyDefaultToAll = () => {
    if (!target) return;
    setInvoiceMembers((prev) =>
      recalculateMemberFees(
        prev,
        trainingDateKeys,
        target.defaultFee,
        target.defaultFeeBasis,
        true,
      ),
    );
  };

  const trainingDateKeys = trainingDates.map(trainingDateKey);

  const canSubmit =
    !issuing &&
    !!target &&
    !!startDate &&
    !!endDate &&
    trainingDates.length > 0 &&
    invoiceMembers.length > 0 &&
    invoiceMembers.some((m) => parseFloat(m.fee || "0") !== 0);

  const handleSubmit = async () => {
    if (!target || !startDate || !endDate) return;
    try {
      setIssuing(true);
      await issueInvoices({
        club_id: target.clubId,
        group_name: target.groupName,
        invoice_period_start: startDate.format("DD/MM/YYYY"),
        invoice_period_end: endDate.format("DD/MM/YYYY"),
        due_date: dueDate ? dueDate.format("DD/MM/YYYY") : undefined,
        training_dates: trainingDates.map((d) => d.format("DD/MM/YYYY HH:mm")),
        members: invoiceMembers.map((m) => ({
          handler_id: m.handler_id,
          fee: parseFloat(m.fee || "0"),
          ...(m.excludedTrainingDates.length > 0
            ? { excluded_training_dates: m.excludedTrainingDates }
            : {}),
        })),
      });
      await onSuccess();
      onClose();
    } catch (err) {
      console.error("Error issuing invoices:", err);
      onError("Failed to issue invoices. Please try again.");
    } finally {
      setIssuing(false);
    }
  };

  const addableMembers = target
    ? target.clubMembers.filter(
        (m) => !invoiceMembers.some((im) => im.handler_id === m.handler_id),
      )
    : [];

  const total = invoiceMembers.reduce(
    (sum, m) => sum + parseFloat(m.fee || "0"),
    0,
  );

  const defaultFeeSummary = target
    ? target.defaultFeeBasis === "session"
      ? `Group default: £${target.defaultFee.toFixed(2)} per session`
      : `Group default: £${target.defaultFee.toFixed(2)} per invoice`
    : "";

  return (
    <Dialog
      open={target != null}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 } } }}
    >
      <DialogHeader
        title="Issue Invoices"
        subtitle={target?.groupName}
        onClose={onClose}
        closeDisabled={issuing}
      />
      <DialogContent dividers sx={{ px: { xs: 1.5, sm: 3 } }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={{ xs: 2, sm: 3 }}>
            <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Invoice Period
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={setStartDate}
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: { fullWidth: true, size: "small" },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={setEndDate}
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: { fullWidth: true, size: "small" },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Due Date (optional)"
                    value={dueDate}
                    onChange={setDueDate}
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        helperText:
                          "Defaults to 7 days from today if left blank",
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>

            <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Training Session Dates
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <TimePicker
                    label="Default time for new dates"
                    value={defaultTrainingTime}
                    onChange={(newValue) => {
                      if (newValue) {
                        setDefaultTrainingTime(
                          newValue.second(0).millisecond(0),
                        );
                      }
                    }}
                    slotProps={{
                      textField: { fullWidth: true, size: "small" },
                    }}
                    format="HH:mm"
                    ampm={false}
                  />
                </Grid>
              </Grid>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", sm: "center" }}
                sx={{ mb: 2 }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <DateTimePicker
                    label="Add Training Date & Time"
                    value={newTrainingDate}
                    onChange={(newValue) => {
                      if (!newValue) {
                        setNewTrainingDate(null);
                        return;
                      }
                      if (newValue.hour() === 0 && newValue.minute() === 0) {
                        setNewTrainingDate(
                          newValue
                            .hour(defaultTrainingTime.hour())
                            .minute(defaultTrainingTime.minute()),
                        );
                      } else {
                        setNewTrainingDate(newValue);
                      }
                    }}
                    slotProps={{
                      textField: { fullWidth: true, size: "small" },
                    }}
                    format="DD/MM/YYYY HH:mm"
                    ampm={false}
                  />
                </Box>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleAddTrainingDate}
                  disabled={!newTrainingDate}
                  size="small"
                  sx={{ flexShrink: 0 }}
                >
                  Add date
                </Button>
              </Stack>
              {trainingDates.length === 0 ? (
                <Alert severity="info">
                  No training dates added. Add dates to include them in the
                  invoice.
                </Alert>
              ) : (
                <Stack spacing={1}>
                  {trainingDates.map((date, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: 1.5,
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        bgcolor: theme.palette.background.paper,
                      }}
                    >
                      <Typography variant="body2">
                        {date.format("DD MMM YYYY, HH:mm")}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveTrainingDate(index)}
                        color="error"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              )}
            </Paper>

            <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: { xs: "flex-start", sm: "center" },
                  justifyContent: "space-between",
                  gap: 1,
                  flexWrap: "wrap",
                  mb: 2,
                }}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Member Fees
                  </Typography>
                  {target && target.defaultFee > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      {defaultFeeSummary}
                    </Typography>
                  )}
                </Box>
                {target && target.defaultFee > 0 && (
                  <Button size="small" onClick={handleApplyDefaultToAll}>
                    Apply default to all
                  </Button>
                )}
              </Box>
              {trainingDateKeys.length > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Uncheck training sessions below a member to exclude those dates
                  from their invoice.
                </Alert>
              )}
              {isMobile ? (
                <Stack spacing={1.25}>
                  {invoiceMembers.map((member) => (
                    <Box
                      key={member.handler_id}
                      sx={{
                        p: 1.25,
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          minWidth: 0,
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: theme.palette.primary.main,
                            fontSize: "0.875rem",
                            flexShrink: 0,
                          }}
                        >
                          {member.handler_name.charAt(0)}
                        </Avatar>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, wordBreak: "break-word" }}
                          >
                            {member.handler_name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "block",
                              wordBreak: "break-all",
                            }}
                          >
                            {member.email_address}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() =>
                            handleRemoveFromInvoice(member.handler_id)
                          }
                          title="Remove from invoice"
                          sx={{ flexShrink: 0 }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                      <TextField
                        label="Fee"
                        value={member.fee}
                        onChange={(e) =>
                          handleUpdateFee(member.handler_id, e.target.value)
                        }
                        type="number"
                        size="small"
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">£</InputAdornment>
                          ),
                        }}
                        fullWidth
                      />
                      <MemberTrainingDates
                        member={member}
                        trainingDateKeys={trainingDateKeys}
                        onToggle={handleToggleMemberTrainingDate}
                      />
                    </Box>
                  ))}
                </Stack>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Member</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">
                          Fee (£)
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: 600 }}
                          align="center"
                          width={80}
                        >
                          Remove
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoiceMembers.map((member) => (
                        <React.Fragment key={member.handler_id}>
                          <TableRow hover>
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Avatar
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    bgcolor: theme.palette.primary.main,
                                    fontSize: "0.875rem",
                                  }}
                                >
                                  {member.handler_name.charAt(0)}
                                </Avatar>
                                {member.handler_name}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {member.email_address}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                value={member.fee}
                                onChange={(e) =>
                                  handleUpdateFee(
                                    member.handler_id,
                                    e.target.value,
                                  )
                                }
                                type="number"
                                size="small"
                                inputProps={{ min: 0, step: 0.01 }}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      £
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{ maxWidth: 120 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() =>
                                  handleRemoveFromInvoice(member.handler_id)
                                }
                                title="Remove from invoice"
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                          {trainingDateKeys.length > 0 && (
                            <TableRow>
                              <TableCell colSpan={4} sx={{ pt: 0, pb: 2 }}>
                                <MemberTrainingDates
                                  member={member}
                                  trainingDateKeys={trainingDateKeys}
                                  onToggle={handleToggleMemberTrainingDate}
                                />
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              <Box
                sx={{
                  mt: 2,
                  p: { xs: 1.25, sm: 2 },
                  bgcolor: theme.palette.grey[100],
                  borderRadius: 1,
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Total: £{total.toFixed(2)}
                </Typography>
              </Box>
            </Paper>

            {addableMembers.length > 0 && (
              <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, mb: 2 }}
                >
                  Add member to invoice
                </Typography>
                <List dense disablePadding>
                  {addableMembers.map((member) => (
                    <ListItem
                      key={member.handler_id}
                      secondaryAction={
                        <Button
                          size="small"
                          startIcon={<Add />}
                          onClick={() => handleAddToInvoice(member)}
                        >
                          Add
                        </Button>
                      }
                      sx={{
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        mb: 1,
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: theme.palette.primary.main,
                            fontSize: "0.875rem",
                          }}
                        >
                          {member.handler_name.charAt(0)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={member.handler_name}
                        secondary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              mt: 0.5,
                            }}
                          >
                            <Email sx={{ fontSize: 14 }} />
                            {member.email_address}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </Stack>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions
        sx={{
          p: { xs: 1.5, sm: 2 },
          flexDirection: { xs: "column-reverse", sm: "row" },
          "& > :not(style) ~ :not(style)": {
            ml: { xs: 0, sm: 1 },
          },
          "& > .MuiButton-root": {
            width: { xs: "100%", sm: "auto" },
          },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Button onClick={onClose} disabled={issuing}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<Send />}
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {issuing ? "Issuing..." : "Issue Invoices"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IssueInvoiceDialog;
